import { useEffect, useState, useRef } from 'react';
import { AlertTriangle, XCircle, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LLMHealthBanner — surfaces real-time LLM key health.
 * Polls /api/health/llm every 30s. Renders only when status != 'ok'.
 * Yellow bar for degraded, red for down. Dismissible for the session.
 */
export default function LLMHealthBanner() {
  const [state, setState] = useState({ status: 'ok', hint: null, last_error_kind: null });
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await axios.get(`${API}/api/health/llm`, { timeout: 8000 });
        if (!cancelled) setState(res.data || { status: 'ok' });
      } catch {
        // Network error — don't flip the banner; absent data is silent-ok
      }
    };
    fetchHealth();
    timerRef.current = setInterval(fetchHealth, 30000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (state.status === 'ok' || dismissed) return null;

  const isDown = state.status === 'down';
  const tone = isDown
    ? 'bg-red-950/90 border-red-500/50 text-red-100'
    : 'bg-amber-950/90 border-amber-500/50 text-amber-100';
  const iconTone = isDown ? 'text-red-400' : 'text-amber-400';
  const Icon = isDown ? XCircle : AlertTriangle;
  const label = isDown ? 'AI services unavailable' : 'AI services degraded';
  const defaultHint = isDown
    ? 'Voice interview and AI replies will fail until the Emergent LLM key is topped up.'
    : 'Voice interviews may be unreliable right now.';

  return (
    <div
      className={`border-b ${tone} px-4 py-2.5 text-xs flex items-start gap-3 backdrop-blur-sm`}
      data-testid="llm-health-banner"
      role="alert"
    >
      <Icon size={16} className={`${iconTone} mt-0.5 flex-shrink-0`} />
      <div className="flex-1 leading-relaxed">
        <strong className="font-semibold">{label}</strong>
        <span className="opacity-90"> — {state.hint || defaultHint}</span>
        {state.last_error_kind === 'balance' && (
          <span className="opacity-90"> Please notify the EvalentX admin to restore service.</span>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="opacity-70 hover:opacity-100 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="llm-health-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
